import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import AccountPickerContext from 'contexts/AccountPickerContext';
import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';
import ToastContext from 'contexts/ToastContext';
import STORE from 'store';
import {
  AgentKbState,
  fetchAgentKbState,
  saveAgentKbArtefact,
} from 'services/agentKb';

// Live KB state for the editor.
//
// Reads split by what can be live: artefact heads, versions and pins are
// Firestore listeners (two staff editing at once must see each other), while
// rights and the bucket's newest version can only come from the server, so they
// arrive once per load from the endpoint's `state` action. Revision bodies are
// immutable, so they are point-read and cached forever by `{id}__{rev}` — a
// save bumps draftRev, which changes the key and re-reads naturally.
//
// Every WRITE goes through the endpoint: the client has no permission to touch
// these collections (see firestore.rules), and the server owns the revision and
// publish invariants.

interface AgentKbContextType {
  meta: AgentKb.Meta | null;
  artefacts: AgentKb.Artefact[];
  versions: AgentKb.Version[];
  pins: AgentKb.Pin[];
  rights: AgentKb.Rights;
  // Newest version folder in the bucket — null when nothing is published or the
  // bucket could not be listed.
  bucketLatestVersion: string | null;
  // Every version folder in the bucket, newest first. Empty when the bucket
  // could not be listed OR when the server predates this field — the two are
  // indistinguishable here, which is why the UI only offers bucket-only actions
  // when this is non-empty.
  bucketVersions: string[];
  // What this account resolves to right now, and the domains that version ships.
  resolvedVersion: AgentKb.ResolvedVersion | null;
  currentUserId: string;
  isLoading: boolean;
  // An upload nobody has imported: the editor's draft does not know about it,
  // so publishing now would quietly drop it.
  isStale: boolean;
  getRevisionBody: (params: {
    artefactId: string;
    rev: number;
  }) => Promise<string | null>;
  saveArtefact: (params: {
    artefactId: string;
    kind: AgentKb.ArtefactKind;
    path: string;
    frontmatter: { [key: string]: string };
    body: string;
    baseRev: number;
  }) => Promise<boolean>;
  // Re-reads the server-only half (rights, bucket version) after an action that
  // could have changed it.
  refreshServerState: () => Promise<void>;
}

const NO_RIGHTS: AgentKb.Rights = {
  canRead: false,
  canEdit: false,
  canPublish: false,
};

export const AgentKbContext = createContext<AgentKbContextType>({
  meta: null,
  artefacts: [],
  versions: [],
  pins: [],
  rights: NO_RIGHTS,
  bucketLatestVersion: null,
  bucketVersions: [],
  resolvedVersion: null,
  currentUserId: '',
  isLoading: true,
  isStale: false,
  getRevisionBody: () => {
    throw new Error('getRevisionBody used outside AgentKbProvider');
  },
  saveArtefact: () => {
    throw new Error('saveArtefact used outside AgentKbProvider');
  },
  refreshServerState: () => {
    throw new Error('refreshServerState used outside AgentKbProvider');
  },
});

const AgentKbProvider = ({ children }: { children: ReactNode }) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { api } = useContext(CloudFunctionClientContext);
  const { showToast } = useContext(ToastContext);

  const [meta, setMeta] = useState<AgentKb.Meta | null>(null);
  const [artefacts, setArtefacts] = useState<AgentKb.Artefact[]>([]);
  const [versions, setVersions] = useState<AgentKb.Version[]>([]);
  const [pins, setPins] = useState<AgentKb.Pin[]>([]);
  const [serverState, setServerState] = useState<AgentKbState | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // Immutable-revision cache; cleared only when the account changes.
  const revisionCacheRef = useRef<Map<string, string | null>>(new Map());

  useEffect(() => {
    let isActive = true;
    const unsubscribe = STORE.agentKb
      .getAgentKbRef({ scope: 'system' })
      .onSnapshot((snapshot) => {
        if (!isActive) {
          return;
        }
        setMeta(snapshot.exists ? snapshot.data() ?? null : null);
      });
    return () => {
      isActive = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    let isActive = true;
    const unsubscribe = STORE.agentKb
      .getAgentKbArtefactsRef({ scope: 'system' })
      .onSnapshot((snapshot) => {
        if (!isActive) {
          return;
        }
        setArtefacts(
          snapshot.docs
            .map((doc) => doc.data())
            .filter((artefact) => !artefact.archived),
        );
      });
    return () => {
      isActive = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    let isActive = true;
    const unsubscribe = STORE.agentKb
      .getAgentKbVersionsRef({ scope: 'system' })
      .onSnapshot((snapshot) => {
        if (!isActive) {
          return;
        }
        setVersions(
          snapshot.docs
            .map((doc) => doc.data())
            .sort((a, b) => b.publishedOn.localeCompare(a.publishedOn)),
        );
      });
    return () => {
      isActive = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    let isActive = true;
    const unsubscribe = STORE.agentKb
      .getAgentKbPinsRef({ scope: 'system' })
      .onSnapshot((snapshot) => {
        if (!isActive) {
          return;
        }
        setPins(snapshot.docs.map((doc) => doc.data()));
      });
    return () => {
      isActive = false;
      unsubscribe();
    };
  }, []);

  const loadServerState = useCallback(async (): Promise<void> => {
    const result = await fetchAgentKbState(api);
    if (result.ok) {
      setServerState(result.data);
    } else {
      showToast(result.error);
    }
    setIsLoading(false);
  }, [api, showToast]);

  useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    revisionCacheRef.current = new Map();
    loadServerState().finally(() => {
      if (!isActive) {
        return;
      }
    });
    return () => {
      isActive = false;
    };
  }, [loadServerState, selectedAccountId]);

  const getRevisionBody = useCallback(
    async ({
      artefactId,
      rev,
    }: {
      artefactId: string;
      rev: number;
    }): Promise<string | null> => {
      if (rev <= 0) {
        return null;
      }
      const cacheKey = `${artefactId}__${rev}`;
      const cached = revisionCacheRef.current.get(cacheKey);
      if (cached !== undefined) {
        return cached;
      }
      const snapshot = await STORE.agentKb
        .getAgentKbRevisionsRef({ scope: 'system' })
        .doc(cacheKey)
        .get();
      const body = snapshot.exists ? snapshot.data()?.body ?? null : null;
      revisionCacheRef.current.set(cacheKey, body);
      return body;
    },
    [],
  );

  const saveArtefact = useCallback(
    async (params: {
      artefactId: string;
      kind: AgentKb.ArtefactKind;
      path: string;
      frontmatter: { [key: string]: string };
      body: string;
      baseRev: number;
    }): Promise<boolean> => {
      const result = await saveAgentKbArtefact(api, params);
      if (!result.ok) {
        // A stale-revision refusal is the common case here, and the message
        // says whose edit won — never discard the author's text on it.
        showToast(result.error);
        return false;
      }
      showToast(`Saved ${params.artefactId}`);
      return true;
    },
    [api, showToast],
  );

  const value = useMemo(() => {
    const bucketLatestVersion = serverState?.bucketLatestVersion ?? null;
    return {
      meta,
      artefacts,
      versions,
      pins,
      rights: serverState?.rights ?? NO_RIGHTS,
      bucketLatestVersion,
      bucketVersions: serverState?.bucketVersions ?? [],
      resolvedVersion: serverState?.resolvedVersion ?? null,
      currentUserId: serverState?.userId ?? '',
      isLoading,
      isStale:
        bucketLatestVersion !== null &&
        bucketLatestVersion !== (meta?.lastImportedVersion ?? null),
      getRevisionBody,
      saveArtefact,
      refreshServerState: loadServerState,
    };
  }, [
    meta,
    artefacts,
    versions,
    pins,
    serverState,
    isLoading,
    getRevisionBody,
    saveArtefact,
    loadServerState,
  ]);

  return (
    <AgentKbContext.Provider value={value}>{children}</AgentKbContext.Provider>
  );
};

export default AgentKbProvider;
